import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { BusStationListComponent } from '../bus-station-list/bus-station-list.component';
import { BusBookingService } from 'src/app/services/bus-booking/bus-booking.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';

@Component({
  selector: 'bus-search-component',
  templateUrl: './bus-search-component.component.html',
  styleUrls: ['./bus-search-component.component.scss'],
})
export class BusSearchComponent implements OnInit {
  public currentHeaderData: any = {
    prevPageLink: '/home',
    title: 'bus.bus_search.header',
  };
  public recentSearches: any[] = [];

  bookBusSearchForm: FormGroup;
  busQueryFields: any = {
    source: '',
    destination: '',
    sourceEntityId: '',
    destinationEntityId: '',
    departureDate: '',
  };
  todayDate: string;
  sourceDestinationSameError: boolean = false;
  private isModalOpen: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private modalController: ModalController,
    private navController: NavController,
    private busBookingService: BusBookingService,
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService
  ) {
    this.todayDate = new Date().toISOString().split('T')[0];
    this.initializeForm();
  }

  ngOnInit() {
    this.loadRecentSearches();
    this.loadSearchQuery();
  }

  ionViewDidEnter() {
    this.loadRecentSearches();
    this.loadSearchQuery();
  }

  private loadRecentSearches() {
    this.recentSearches = this.busBookingService.resentSearches
      ? [...JSON.parse(this.busBookingService.resentSearches)]
      : [];
  }

  private loadSearchQuery() {
    try {
      const queryUrl = this.activatedRoute.snapshot.params.queryUrl;
      if (queryUrl) {
        const data = JSON.parse(decodeURIComponent(queryUrl));
        this.onRecentSearchSelect(data);
      } else if (this.busBookingService.busSearchQuery) {
        this.busQueryFields = JSON.parse(this.busBookingService.busSearchQuery);
        if (this.busQueryFields?.fq) {
          delete this.busQueryFields.fq;
          this.busBookingService.busSearchQuery = JSON.stringify(
            this.busQueryFields
          );
        }
        this.bookBusSearchForm.patchValue({
          source: this.busQueryFields.source,
          destination: this.busQueryFields.destination,
          departureDate: this.busQueryFields.departureDate,
        });
      }
    } catch (error) {
      console.error('Error parsing query URL:', error);
    }
  }

  initializeForm() {
    this.bookBusSearchForm = this.formBuilder.group({
      source: ['', Validators.required],
      destination: ['', Validators.required],
      departureDate: ['', [Validators.required, this.dateValidator.bind(this)]],
    });
  }

  dateValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }

    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      return { pastDate: true };
    }

    return null;
  }

  async getStation(type: 'source' | 'destination') {
    if (this.isModalOpen) {
      return;
    }
    this.isModalOpen = true;

    this.analyticsService.trackUnified('search_input_clicked', {
      flow: 'bus',
      outcome: 'success',
      legacy_event_name: 'Bus Search Input Clicked',
      type: type,
      button: 'Select ' + type + ' Station',
    });
    const modal = await this.modalController.create({
      component: BusStationListComponent,
      backdropDismiss: false,
    });

    modal.onDidDismiss().then(async (data) => {
      this.isModalOpen = false;
      if (data.data) {
        const selectedCity = data?.data?.busStation;
        const cityDisplayName = `${selectedCity.city}, ${selectedCity.state}`;

        if (type === 'source') {
          this.busQueryFields.source = cityDisplayName;
          this.busQueryFields.sourceEntityId = selectedCity.entityId;
          this.bookBusSearchForm.get('source')?.setValue(cityDisplayName);
        }
        if (type === 'destination') {
          this.busQueryFields.destination = cityDisplayName;
          this.busQueryFields.destinationEntityId = selectedCity.entityId;
          this.bookBusSearchForm.get('destination')?.setValue(cityDisplayName);
        }
      } else {
        if (type === 'source' && !this.busQueryFields?.source) {
          this.bookBusSearchForm.get('source')?.markAsTouched();
          this.bookBusSearchForm.get('source')?.updateValueAndValidity();
        }
        if (type === 'destination' && !this.busQueryFields?.destination) {
          this.bookBusSearchForm.get('destination')?.markAsTouched();
          this.bookBusSearchForm.get('destination')?.updateValueAndValidity();
        }
      }
      this.busBookingService.busSearchQuery = JSON.stringify(
        this.busQueryFields
      );
    });

    return await modal.present();
  }

  onDepartureDateChange(event: any) {
    if (this.busQueryFields.departureDate !== event) {
      this.analyticsService.trackUnified('departure_date_changed', {
        flow: 'bus',
        outcome: 'success',
        legacy_event_name: 'Bus Departure Date Changed',
        departure_date: event,
      });
      this.busQueryFields.departureDate = event;
      this.bookBusSearchForm
        .get('departureDate')
        ?.setValue(event, { emitEvent: false });
      this.busBookingService.busSearchQuery = JSON.stringify(
        this.busQueryFields
      );
    }
  }

  selectToday() {
    const today = new Date().toISOString().split('T')[0];
    this.bookBusSearchForm.patchValue({
      departureDate: today,
    });
    this.busQueryFields.departureDate = today;
  }

  selectTomorrow() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowDate = tomorrow.toISOString().split('T')[0];
    this.bookBusSearchForm.patchValue({
      departureDate: tomorrowDate,
    });
    this.busQueryFields.departureDate = tomorrowDate;
  }

  isSourceDestinationSame(): boolean {
    const source = this.busQueryFields?.sourceEntityId;
    const destination = this.busQueryFields?.destinationEntityId;
    this.sourceDestinationSameError =
      source && destination && source === destination;
    return this.sourceDestinationSameError;
  }

  switchSourceDestination() {
    if (this.busQueryFields?.source && this.busQueryFields?.destination) {
      [this.busQueryFields.source, this.busQueryFields.destination] = [
        this.busQueryFields.destination,
        this.busQueryFields.source,
      ];
      [
        this.busQueryFields.sourceEntityId,
        this.busQueryFields.destinationEntityId,
      ] = [
        this.busQueryFields.destinationEntityId,
        this.busQueryFields.sourceEntityId,
      ];
      this.bookBusSearchForm
        .get('source')
        ?.setValue(this.busQueryFields.source);
      this.bookBusSearchForm
        .get('destination')
        ?.setValue(this.busQueryFields.destination);
      this.busBookingService.busSearchQuery = JSON.stringify(
        this.busQueryFields
      );
    }
  }

  handleSearch() {
    this.bookBusSearchForm.markAllAsTouched();
    this.saveRecentSearches();
    if (this.bookBusSearchForm.valid && !this.sourceDestinationSameError) {
      const cleanQuery = { ...this.busQueryFields };
      if (cleanQuery?.fq) {
        delete cleanQuery.fq;
      }
      if (cleanQuery?.selectedSortOption) {
        delete cleanQuery.selectedSortOption;
      }
      this.analyticsService.trackUnified('search_started', {
        flow: 'bus',
        step: 'search',
        step_index: 1,
        outcome: 'success',
        legacy_event_name: 'Bus Search Button Clicked',
        source: cleanQuery.source,
        destination: cleanQuery.destination,
        source_entity_id: cleanQuery.sourceEntityId,
        destination_entity_id: cleanQuery.destinationEntityId,
        departure_date: cleanQuery.departureDate,
      });
      this.busBookingService.busSearchQuery = JSON.stringify(cleanQuery);
      this.navController.navigateForward([
        '/bus/search-results',
        {
          queryUrl: encodeURIComponent(JSON.stringify(cleanQuery)),
        },
      ]);
    }
  }

  saveRecentSearches() {
    const getCityName = (cityState: string) => cityState.split(',')[0].trim();

    const resentSearchSave = {
      departureDate: this.busQueryFields.departureDate,
      destination: getCityName(this.busQueryFields.destination),
      destinationEntityId: this.busQueryFields.destinationEntityId,
      source: getCityName(this.busQueryFields.source),
      sourceEntityId: this.busQueryFields.sourceEntityId,
    };

    let recentSearches = this.busBookingService.resentSearches
      ? [...JSON.parse(this.busBookingService.resentSearches)]
      : [];

    const alreadyExists = recentSearches.some(
      (search) =>
        search.departureDate === resentSearchSave.departureDate &&
        search.destination === resentSearchSave.destination &&
        search.destinationEntityId === resentSearchSave.destinationEntityId &&
        search.source === resentSearchSave.source &&
        search.sourceEntityId === resentSearchSave.sourceEntityId
    );

    if (!alreadyExists) {
      recentSearches = [resentSearchSave, ...recentSearches].slice(0, 3);
      this.recentSearches = recentSearches;
      this.busBookingService.resentSearches = JSON.stringify(recentSearches);
    }
  }

  onRecentSearchSelect(search: any) {
    this.busQueryFields = { ...search };
    this.bookBusSearchForm.patchValue({
      source: search.source,
      destination: search.destination,
      departureDate: search.departureDate,
    });
  }
}
